import React from 'react';
import { WeezHeader, Button, Icon, AnchorNav, ToastContext } from '@weezevent/nacre';
import {
    convertMenhirToPyvarPipeTranslate,
    converPyvarToMenhirPipeTranslate,
    cleanSingleTranslation,
    addDefaultLanguageCode
} from '../../../components/utils/pipeTranslateConvert';
import { useTranslation } from 'react-i18next';
import { DisplayModuleMessages } from './displayModuleMessages';
import Display from './display';
import Cgv from './cgv';
import Language from './language';
import { useForm } from '@weezevent/weezjs-core';
import EventSettings from '../../../models/eventSettings';
import Loading from '../../../components/loading';
import PropTypes from 'prop-types';
import EventModel from '../../../models/eventModel';
import Style from './module-display.module.css';
import { LANGUAGES_CODES } from '../../../nacre/components/LanguageSelector';
import { unifyLanguageCodes } from '../../../components/utils/locales';

export default function ModuleDisplay({ organization, event }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [defaultLanguage, setDefaultLanguage] = React.useState([]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const keys = ['instructions_form', 'text_confirmation_inscription', 'text_perso_no_ticket', 'text_perso_quota_reached'];

    const translateLanguageModuleToLanguageOptions = (language, languages) => {
        if (languages) {
            let ret = languages[parseInt(language, 10)];
            unifyLanguageCodes(ret);
        }
    };

    const translateIdtoChoice = languageId => {
        for (let lang in LANGUAGES_CODES) {
            if (LANGUAGES_CODES[lang] === parseInt(languageId)) {
                return lang;
            }
        }
        return '';
    };

    const [formModuleDisplay, setFormModuleDisplay, setFormObject] = useForm({
        instructions_form: null,
        text_confirmation_inscription: null,
        text_perso_no_ticket: null,
        text_perso_quota_reached: null,
        aff_date_finvente: false,
        categories_expanded: false,

        aff_quantite: false,
        aff_commissions: false,
        aff_ht: false,
        display_tarif_description: false,
        display_list_participants_widget: 0,
        display_widget_timer: false,
        widget_timer_duration: '0',
        type_cgv: false,
        id_langue: 0,
        cgv: ''
    });

    const [toggleStates, setToggleStates, setToggleStatesObject] = useForm({
        instructions_form: false,
        text_confirmation_inscription: false,
        text_perso_no_ticket: false,
        text_perso_quota_reached: false
    });

    const [eventModel, loadingEvent] = EventModel.useApiModel(
        {
            id: event.pk,
            organizationId: organization.id
        },
        []
    );

    const [settings, loading, sync] = EventSettings.useApiModel({
        organizationId: organization.pk,
        eventId: event.pk,
        cache: false,
        allow_cache: false,
        onSuccess: data => {
            let datas = { ...formModuleDisplay };
            // We have to replace following states because pyvar API is a proxy and weez language key are with _
            keys.forEach(key => {
                if (data.values[key]?.length) {
                    const converwd = convertMenhirToPyvarPipeTranslate(data.values[key]);
                    const lngCode = unifyLanguageCodes([translateIdtoChoice(data.values.id_langue)])[0];
                    datas[key] = addDefaultLanguageCode(converwd, lngCode);
                } else {
                    datas[key] = '';
                }
            });

            datas['aff_date_finvente'] = data.values.aff_date_finvente === '0' ? false : true;
            datas['aff_commissions'] = data.values.aff_commissions === '0' ? false : true;
            datas['categories_expanded'] = data.values.categories_expanded === '0' ? false : true;
            datas['aff_quantite'] = data.values.aff_quantite === '0' ? false : true;
            datas['aff_ht'] = data.values.aff_ht === '0' ? false : true;
            datas['display_tarif_description'] = data.values.display_tarif_description === '0' ? false : true;
            datas['display_list_participants_widget'] = parseInt(data.values.display_list_participants_widget);

            datas['display_widget_timer'] = data.values.display_widget_timer === '0' ? false : true;
            datas['widget_timer_duration'] = data.values.widget_timer_duration;
            datas['type_cgv'] = data.values.type_cgv === '0' ? false : true;
            datas['cgv'] = data.values.cgv;
            datas['id_langue'] = data.values.id_langue;

            const defaultToggleStates = keys.reduce((states, key) => {
                states[key] = Boolean(datas[key]?.length > 0);
                return states;
            }, {});
            setToggleStatesObject(defaultToggleStates);
            setFormObject(datas);
            setDefaultLanguage(translateIdtoChoice(data.values.id_langue));
        }
    });

    const onSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        const lngCode = unifyLanguageCodes([defaultLanguage])[0];

        const customMessages = keys.reduce((messages, key) => {
            messages[key] = toggleStates[key] ? converPyvarToMenhirPipeTranslate(cleanSingleTranslation(formModuleDisplay[key], lngCode)) : '';
            return messages;
        }, {});
        const newSettings = new EventSettings({
            ...settings,
            values: {
                ...settings.values,
                aff_date_finvente: formModuleDisplay['aff_date_finvente'] ? '1' : '0',
                categories_expanded: formModuleDisplay['categories_expanded'] ? '1' : '0',
                aff_quantite: formModuleDisplay['aff_quantite'] ? '1' : '0',
                aff_commissions: formModuleDisplay['aff_commissions'] ? '1' : '0',
                aff_ht: formModuleDisplay['aff_ht'] ? '1' : '0',
                display_tarif_description: formModuleDisplay['display_tarif_description'] ? '1' : '0',
                display_list_participants_widget: formModuleDisplay['display_list_participants_widget'],
                display_widget_timer: formModuleDisplay['display_widget_timer'] ? '1' : '0',
                type_cgv: formModuleDisplay['cgv'] ? '2' : '0',
                cgv: formModuleDisplay['cgv'],
                id_langue: formModuleDisplay['id_langue'],
                widget_timer_duration: formModuleDisplay['widget_timer_duration'],

                // We have to replace following states because pyvar API is a proxy and weez language key are with _
                ...customMessages
            }
        });
        newSettings
            .save({
                organizationId: organization.pk,
                eventId: event.pk
            })
            .then(() => {
                setIsSubmitting(false);
                sync({ cache: false, allow_cache: false });
                toast.success(t(`ticket.event.integration.success-toast`));
            });
    }, [settings, defaultLanguage, toggleStates, formModuleDisplay, sync, toast, t, organization, event]);

    if (loading && loadingEvent) {
        return <Loading />;
    }
    return (
        <>
            {!loading && !loadingEvent && (
                <div className={'module-display-container'}>
                    <WeezHeader
                        title={t('ticket.ticket_option.module.title')}
                        rightComponent={[
                            <a
                                key="secondary"
                                rel="noopener noreferrer"
                                target="_blank"
                                href={`${window.weezConfig.api.widgetUrl}/ticket/E${event.id}/?code=${event.default_widget_access_code}&locale=${defaultLanguage}#weez_preview`}
                            >
                                <Button
                                    className={Style['preview-button']}
                                    primary={false}
                                    icon={<Icon className={Style['cta-save-icon']} name="eye" margin={false} />}
                                    label={
                                        <div>
                                            <Icon name={'eye'} />
                                            {t('ticket.ticket_option.module.view_widget')}
                                        </div>
                                    }
                                />
                            </a>,
                            <Button
                                primary
                                key="primary"
                                icon={<Icon className={Style['cta-save-icon']} name="save" margin={false} />}
                                label={t('ticket.common.action.save')}
                                onClick={onSubmit}
                                isLoading={isSubmitting}
                            />
                        ]}
                    />
                    <AnchorNav
                        rootId="main"
                        items={[
                            {
                                id: 'message-information',
                                title: t('ticket.ticket_option.module.message_information.title')
                            },
                            {
                                id: 'display',
                                title: t('ticket.ticket_option.module.display.title')
                            },
                            {
                                id: 'cgv',
                                title: t('ticket.ticket_option.module.cgv.title')
                            },
                            {
                                id: 'language',
                                title: t('ticket.ticket_option.module.language.title')
                            }
                        ]}
                    >
                        <DisplayModuleMessages
                            toggleStates={toggleStates}
                            setToggleStates={setToggleStates}
                            formModuleDisplay={formModuleDisplay}
                            setFormModuleDisplay={setFormModuleDisplay}
                            translateIdtoChoice={translateLanguageModuleToLanguageOptions}
                            languages={eventModel?.languages}
                            defaultLang={defaultLanguage}
                        />
                        <Display formModuleDisplay={formModuleDisplay} setFormModuleDisplay={setFormModuleDisplay} />
                        <Cgv form={formModuleDisplay} onChangeForm={setFormModuleDisplay} />
                        <Language
                            formModuleDisplay={formModuleDisplay}
                            setFormModuleDisplay={setFormModuleDisplay}
                            languages={eventModel?.languages}
                            translateIdtoChoice={translateIdtoChoice}
                            setDefaultLanguage={setDefaultLanguage}
                        />
                    </AnchorNav>
                </div>
            )}
        </>
    );
}

ModuleDisplay.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired
};
